/**
 * Proactive token refresh hook.
 *
 * Schedules a silent refresh ~2 minutes before the access token expires
 * so the user never sees a 401 during normal usage and never gets logged out
 * unexpectedly.
 */

import { useAuthStore } from '@/store/auth'
import { useEffect, useRef } from 'react'

/** How many seconds before expiry to trigger a proactive refresh. */
const REFRESH_BUFFER_SEC = 120 // 2 minutes

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

export function useTokenRefresh() {
  const token = useAuthStore((s) => s.token)
  const refreshToken = useAuthStore((s) => s.refreshToken)
  const tokenExpiresAt = useAuthStore((s) => s.tokenExpiresAt)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const setTokens = useAuthStore((s) => s.setTokens)
  const logout = useAuthStore((s) => s.logout)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }

    if (!isAuthenticated || !token || !refreshToken) return

    // Calculate delay until we should refresh
    let delaySec: number

    if (tokenExpiresAt) {
      const nowSec = Math.floor(Date.now() / 1000)
      delaySec = tokenExpiresAt - nowSec - REFRESH_BUFFER_SEC
    } else {
      // No expiry info — fall back to refreshing in 13 minutes
      // (access tokens default to 15 min TTL)
      delaySec = 13 * 60
    }

    // If already expired or about to expire, refresh immediately
    if (delaySec < 0) delaySec = 0

    timerRef.current = setTimeout(async () => {
      const state = useAuthStore.getState()
      if (!state.isAuthenticated || !state.refreshToken) return

      try {
        const response = await fetch(`${API_BASE_URL}/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: state.refreshToken }),
        })

        if (!response.ok) {
          // Refresh failed — don't logout yet; let the 401 interceptor handle it
          // on the next actual request.
          return
        }

        const data = (await response.json()) as {
          accessToken: string
          refreshToken: string
          accessTokenExpiresAt?: number
        }
        setTokens(data.accessToken, data.refreshToken, data.accessTokenExpiresAt)
      } catch {
        // Network error during proactive refresh — silently ignore.
        // The 401 interceptor will handle it on the next real request.
      }
    }, delaySec * 1000)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [token, refreshToken, tokenExpiresAt, isAuthenticated, setTokens, logout])
}
